<script setup lang="ts">
import { $getNodeByKey, type LexicalNode } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import AttachmentActions from "~/components/dumb/AttachmentActions.vue";
import AttachmentImage from "~/components/dumb/AttachmentImage.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import { AttachmentFieldIcon, AudioIcon, ImageIcon, VideoIcon } from "~/icons";
import { EditorMode, ViewKind } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { downloadFile } from "~/utils/api";
import { asyncState } from "~/utils/async";
import { isAudioType, isImageType, isVideoType } from "~/utils/media";

import PageLink from "../plugins/PageLink.vue";
import type { AttachmentNode } from "./AttachmentNode";

// TODO inline this type when possible
type IsAttachmentNode = (node?: LexicalNode) => node is AttachmentNode;

const props = defineProps<{
  attachmentDuid: string;
  collapsed: boolean;
  nodeKey: string;
  isAttachmentNode: IsAttachmentNode;
  widthPx: number | null;
}>();

const editor = useLexicalComposer();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const editable = ref(editor.isEditable());

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = editor.registerEditableListener((isEditable) => {
    editable.value = isEditable;
  });
});
onUnmounted(() => {
  unregisterListeners();
});

const { state: attachment, isLoading } = asyncState(() => dataStore.getAttachmentByDuid(props.attachmentDuid), {
  default: dataStore.getAttachmentByDuidIfLoaded(props.attachmentDuid),
  watch: () => props.attachmentDuid,
});
const isImage = computed(() => !!attachment.value?.kind && isImageType(attachment.value.kind));
const isVideo = computed(() => !!attachment.value?.kind && isVideoType(attachment.value.kind));
const isAudio = computed(() => !!attachment.value?.kind && isAudioType(attachment.value.kind));
const isMedia = computed(() => isImage.value || isVideo.value || isAudio.value);
const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const downloadAttachment = () => {
  if (!attachment.value) {
    return;
  }

  downloadFile(attachment.value.fileUrl, attachment.value.name);
};

const fullscreenAttachment = () => {
  if (!attachment.value) {
    return;
  }

  appStore.setFullscreenMediaModalOpen({
    attachmentFileUrl: attachment.value.fileUrl,
    attachmentName: attachment.value.name,
    kind: attachment.value.kind,
    entityName: appStore.taskOpenInDetail?.title ?? appStore.docOpenInFullscreen?.title ?? "",
  });
};

const onAttachmentChipClick = () => {
  if (isMedia.value && props.collapsed) {
    return;
  }

  downloadAttachment();
};

const collapseNode = (collapse: boolean) => {
  editor.update(() => {
    const node = $getNodeByKey(props.nodeKey);
    if (node === null || !props.isAttachmentNode(node)) {
      return;
    }

    node.setCollapsed(collapse);
  });
};

const removeAttachment = () => {
  editor.update(() => {
    const node = $getNodeByKey(props.nodeKey);
    if (!node) {
      return;
    }

    node.remove();
  });
};

const setWidthPx = (widthPx: number | null) => {
  editor.update(() => {
    const node = $getNodeByKey(props.nodeKey);

    if (node === null || !props.isAttachmentNode(node)) {
      return;
    }
    node.setWidthPx(widthPx);
  });
};
</script>

<template>
  <div
    v-if="attachment"
    class="!inline-flex"
    :class="[!isMedia || (collapsed && 'align-middle'), isAudio && 'w-full', isLoading && 'align-middle']">
    <LoadingSpinner v-if="isLoading" x-small />
    <AttachmentImage
      v-else-if="isMedia && !collapsed"
      :class="[isAudio && 'w-full']"
      :attachment="attachment"
      :editor-mode="EditorMode.DOC"
      :not-editable="!editable"
      :resizable="editable && isImage"
      :width-px="widthPx"
      @remove="removeAttachment"
      @collapse="collapseNode(true)"
      @resize="setWidthPx" />
    <PageLink
      v-else
      :icon="isImage ? ImageIcon : isVideo ? VideoIcon : isAudio ? AudioIcon : AttachmentFieldIcon"
      :title="attachment.name"
      shrink
      class="!inline-flex align-middle"
      :class="isMedia && collapsed && '!cursor-default'"
      :disable-hover="isMedia && collapsed"
      @click="onAttachmentChipClick">
      <template v-if="isMedia && collapsed">
        <div
          class="pointer-events-none absolute inset-y-px right-0.5 z-10 hidden items-stretch rounded-sm opacity-0 group-hover/page-link:pointer-events-auto group-hover/page-link:flex group-hover/page-link:opacity-100">
          <AttachmentActions
            class="h-full"
            :show-fullscreen="!pageStore.isPublicForm"
            :show-collapse-expand="editable"
            :show-delete="editable && !pageStore.isPublicView && !isTrash"
            collapse-expand-text="Expand"
            collapse-expand-icon="expand"
            opaque-background
            opaque-button-hover
            static-position
            @fullscreen="fullscreenAttachment"
            @download="downloadAttachment"
            @collapse-expand="collapseNode(false)"
            @delete="removeAttachment" />
        </div>
      </template>
    </PageLink>
  </div>
</template>
